import React, { useCallback, useRef, useState } from 'react';
import {
  Bold, Building2, Check, ChevronRight, FileText, Heading, Link2, List, X,
} from 'lucide-react';

import { useXomicsAuth } from './AuthContext';
import { loadOfficePolicies, saveOfficePolicy } from './api';
import {
  Empty, Failed, Head, Loading, useLeave,
} from './parts';
import { OfficeNamed, StoreOfficeLine, useStoreOffice } from './StoreOffice';
import { errorMessage } from './ui';
import { useLiveResource } from './useLive';
import { tx } from './i18n';
import './products.css';

// Policies: the storefront's privacy policy, terms, refund policy and
// shipping policy as one office writes them. A visitor reads the ones of the
// office their country belongs to, so each office has its own text of each.
//
// A text is plain words with a few marks, which the buttons over the box put
// in: an empty line for a new paragraph, "### " for a heading, "• " for a list
// item, **bold**, [words](address), and a line that says [offices] where the
// page lists the offices. The storefront turns them into the page.

const OFFICES_MARK = '[offices]';
const NEW_ADDRESS = 'https://';

function dayOf(time) {
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(time));
}

// A mark around what is selected (or around a word to write over), with
// `select` saying which part of what was put in is selected afterwards.
function wrapSelection(box, value, onChange, build) {
  const from = box.selectionStart ?? value.length;
  const to = box.selectionEnd ?? from;
  const { text, select } = build(value.slice(from, to));
  onChange(`${value.slice(0, from)}${text}${value.slice(to)}`);
  window.requestAnimationFrame(() => {
    box.focus();
    box.setSelectionRange(from + select[0], from + select[1]);
  });
}

// A mark in front of every line the selection touches.
function markLines(box, value, onChange, mark) {
  const from = value.lastIndexOf('\n', (box.selectionStart ?? 0) - 1) + 1;
  const end = value.indexOf('\n', box.selectionEnd ?? from);
  const to = end < 0 ? value.length : end;
  const lines = value.slice(from, to).split('\n').map((line) => (line.startsWith(mark) ? line : `${mark}${line}`)).join('\n');
  onChange(`${value.slice(0, from)}${lines}${value.slice(to)}`);
  window.requestAnimationFrame(() => { box.focus(); box.setSelectionRange(from, from + lines.length); });
}

// One policy as one office writes it. Nothing changes until Save.
function PolicyEditor({
  policy, office, mayManage, onClose, onSaved,
}) {
  const box = useRef(null);
  const kept = policy.body ?? '';
  const [text, setText] = useState(kept);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const touched = text !== kept;

  const close = useLeave(busy, touched, onClose);

  async function save(event) {
    event.preventDefault();
    if (text.trim() === '') { setError(tx('ui.policies.error.empty')); return; }
    if (!touched) { onClose(); return; }
    setBusy(true);
    setError('');
    try {
      await saveOfficePolicy(office.id, policy.code, text);
      onSaved();
    } catch (caught) {
      setError(errorMessage(caught));
      setBusy(false);
    }
  }

  const disabled = !mayManage || busy;
  const sample = tx('ui.hub.tools.words');
  const tools = [
    [tx('ui.hub.tools.bold'), Bold, () => wrapSelection(box.current, text, setText, (picked) => {
      const words = picked || sample;
      return { text: `**${words}**`, select: [2, 2 + words.length] };
    })],
    [tx('ui.policies.tools.heading'), Heading, () => markLines(box.current, text, setText, '### ')],
    [tx('ui.hub.tools.list'), List, () => markLines(box.current, text, setText, '• ')],
    // The address is what is left selected, to be written over.
    [tx('ui.hub.tools.link'), Link2, () => wrapSelection(box.current, text, setText, (picked) => {
      const words = picked || sample;
      return { text: `[${words}](${NEW_ADDRESS})`, select: [words.length + 3, words.length + 3 + NEW_ADDRESS.length] };
    })],
    // Where the page lists the offices: a line of its own.
    [tx('ui.policies.tools.offices'), Building2, () => wrapSelection(box.current, text, setText, () => (
      { text: `\n\n${OFFICES_MARK}\n\n`, select: [2, 2 + OFFICES_MARK.length] }))],
  ];

  return (
    <div className="xo-product-modal-backdrop" role="presentation" onMouseDown={close}>
      <form className="xo-pc-form is-text" role="dialog" aria-modal="true" aria-labelledby="xo-policy-form-title"
        onSubmit={save} onMouseDown={(event) => event.stopPropagation()}>
        <header className="xo-pc-form-head">
          <span className="xo-pc-thumb is-icon"><FileText aria-hidden="true" /></span>
          <div>
            <h2 id="xo-policy-form-title">{policy.name}</h2>
            <OfficeNamed office={office} />
          </div>
          <button type="button" className="xo-icon-btn" aria-label={tx("ui.products.close_bbfa773e")} disabled={busy} onClick={close}>
            <X aria-hidden="true" /></button>
        </header>
        <div className="xo-pc-form-body xo-pol-body">
          {mayManage && (
            <div className="xo-pol-tools" role="toolbar" aria-label={policy.name}>
              {tools.map(([label, Icon, press]) => (
                <button type="button" key={label} title={label} aria-label={label} disabled={disabled} onClick={press}>
                  <Icon aria-hidden="true" /></button>
              ))}
            </div>
          )}
          <textarea ref={box} className="xo-pol-text" aria-label={policy.name} value={text} disabled={disabled} dir="auto"
            onChange={(event) => setText(event.target.value)} />
        </div>
        <footer className="xo-pc-form-foot">
          {error ? <p className="xo-alert t-bad" role="alert">{error}</p> : <span />}
          <button type="button" className="xo-btn-2" disabled={busy} onClick={close}>
            {mayManage ? tx('ui.common.cancel') : tx("ui.products.close_bbfa773e")}</button>
          {mayManage && <button type="submit" className="xo-btn" disabled={busy}>
            {busy ? tx('ui.products.settings.saving') : tx('ui.products.settings.save')}</button>}
        </footer>
      </form>
    </div>
  );
}

export default function XomicsPolicies() {
  const { permissions } = useXomicsAuth();
  const mayManage = permissions.includes('CONTENT_MANAGE');
  const store = useStoreOffice();
  const office = store.office;
  const officeId = office?.id ?? null;
  const list = useLiveResource(['products'], useCallback(() => loadOfficePolicies(officeId), [officeId]), { enabled: officeId !== null });
  const [openCode, setOpenCode] = useState(null);
  const [notice, setNotice] = useState('');

  const head = <Head title={tx('ui.nav.policies')} under={<StoreOfficeLine store={store} />} />;

  if (store.error) return <Failed message={store.error} onRetry={store.refresh} />;
  if (store.loading) return <Loading label={tx('ui.policies.reading')} />;
  // An account whose location keeps no store has no policies to write.
  if (office === null) return <div className="xo-products-page xo-pc-page">{head}<Empty title={tx('ui.store.no_office')} /></div>;
  if (list.error) return <Failed message={list.error} onRetry={list.refresh} />;
  // Another office was chosen: its policies are shown once they are read, never the last one's under its name.
  if (list.data === null || list.data.officeId !== officeId) return <Loading label={tx('ui.policies.reading')} />;

  const open = list.data.policies.find((policy) => policy.code === openCode) ?? null;

  return (
    <div className="xo-products-page xo-pc-page">
      {head}
      {notice && <p className="xo-alert t-ok" role="status"><Check aria-hidden="true" />{notice}</p>}
      {list.data.policies.length === 0 ? <Empty title={tx('ui.policies.none')} /> : (
        <div className="xo-pc-list is-policies">
          <div className="xo-pc-row is-head" aria-hidden="true">
            <span>{tx('ui.policies.policy')}</span>
            <span>{tx('ui.policies.changed')}</span>
            <span />
          </div>
          {list.data.policies.map((policy) => (
            <button type="button" key={policy.code} className={`xo-pc-row${policy.body ? ' is-on' : ''}`}
              onClick={() => { setNotice(''); setOpenCode(policy.code); }}>
              <span className="xo-pc-name">
                <span className="xo-pc-thumb is-icon"><FileText aria-hidden="true" /></span>
                <span><strong>{policy.name}</strong></span>
              </span>
              <span className="xo-pc-number">{policy.body ? dayOf(policy.updatedAt) : tx('ui.policies.not_written')}</span>
              <ChevronRight aria-hidden="true" />
            </button>
          ))}
        </div>
      )}
      {open !== null && <PolicyEditor key={`${officeId}-${open.code}`} policy={open} office={office} mayManage={mayManage}
        onClose={() => setOpenCode(null)}
        onSaved={() => { setOpenCode(null); setNotice(tx('ui.store.saved', { name: open.name })); list.refresh(); }} />}
    </div>
  );
}
